import { getApiHeaders } from '../utils/apiHeaders';

// CRA env vars are inlined at build time.
// Keep the value lookup inside a function so tests/mocks can set process.env before calling.
const getAiBaseUrl = () => process.env.REACT_APP_AI_BACKEND_BASE_URL;
const AI_TOKEN = process.env.REACT_APP_AI_DESTINATIONS_TOKEN;

export type DestinationAIDetails = {
  city: string;
  cityCode: string;
  latitude: string;
  longitude: string;
  description: string;
  topAttractions: Array<{
    name: string;
    description: string;
    latitude: string;
    longitude: string;
  }>;
  howToReach: Partial<{
    air: { title: 'By Air'; description: string; details: string[] };
    train: { title: 'By Train'; description: string; details: string[] };
    road: { title: 'By Road'; description: string; details: string[] };
    cruise: { title: 'By Cruise'; description: string; details: string[] };
  }>;
  visaRequirements: string[];
  visaDetails: Array<{ category: string; validity: string; fee: string }>;
};

export type WebsiteV2DestinationDraftInput = {
  title: string;
  countryOrRegion?: string;
  tags?: string[];
  audience?: string;
};

export type WebsiteV2DestinationDraftResult = {
  requestId?: string;
  provider?: string;
  model?: string;
  generatedFields?: string[];
  draft: {
    slug?: string;
    title?: string;
    countryOrRegion?: string;
    summary?: string;
    tags?: string[];
    hero?: {
      image?: { url?: string; alt?: string };
      eyebrow?: string;
      headline?: string;
      subheadline?: string;
    };
    gallery?: Array<{ url?: string; alt?: string }>;
    story?: string[];
    bestFor?: string[];
    bestTimeToVisit?: string;
    idealDuration?: string;
    travelFeel?: string;
    primeIndex?: {
      score?: number;
      verdict?: string;
      categories?: Array<{ label?: string; score?: number }>;
      quote?: string;
    };
    dossier?: Array<{ label?: string; value?: string }>;
    editorNote?: {
      body?: string;
      authorName?: string;
      authorRole?: string;
    };
    routeHighlights?: Array<{ title?: string; stopLabel?: string; description?: string }>;
    recommendedExperiences?: Array<{ title?: string; description?: string; image?: { url?: string; alt?: string } }>;
    recommendedJourneySlugs?: string[];
    seo?: {
      title?: string;
      description?: string;
      ogImage?: { url?: string; alt?: string };
    };
  };
};

const ensureResponse = async (response: Response) => {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `AI API error: ${response.status}`);
  }
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const body = await response.text();
    throw new Error(
      `AI API returned non-JSON response (content-type: ${contentType || 'unknown'}). ` +
        `This usually means REACT_APP_AI_BACKEND_BASE_URL is pointing to the React dev server ` +
        `or another service returning HTML. Response starts with: ${JSON.stringify(body.slice(0, 80))}`
    );
  }
  return response.json();
};

export const generateWebsiteV2DestinationDraft = async (
  payload: WebsiteV2DestinationDraftInput
): Promise<WebsiteV2DestinationDraftResult> => {
  const AI_BASE_URL = getAiBaseUrl();
  if (!AI_BASE_URL) {
    throw new Error(
      [
        'Missing REACT_APP_AI_BACKEND_BASE_URL.',
        'Create query-app/.env (copy from query-app/.env.example) and restart the dev server.',
        'Example: REACT_APP_AI_BACKEND_BASE_URL=http://localhost:3000',
      ].join(' ')
    );
  }

  const endpoint = `${AI_BASE_URL.replace(/\/$/, '')}/website-v2/destinations/ai-draft`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      ...getApiHeaders('json'),
      ...(AI_TOKEN ? { Authorization: `Bearer ${AI_TOKEN}` } : {})
    },
    body: JSON.stringify(payload)
  });

  const data = await ensureResponse(res);
  return {
    requestId: data?.requestId,
    provider: data?.metadata?.provider,
    model: data?.metadata?.model,
    generatedFields: data?.metadata?.generatedFields,
    draft: data?.draft || {}
  };
};
